import React, {useEffect, useMemo, useRef, useState} from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {BrowserRouter, Link, Route, Routes} from 'react-router-dom';
import './App.css';

import {SESSION_KEY} from './auth/session';
import {COUNTRY_LL, FETCH_RSS_URL, MINERALS, PAGE_SZ, REFRESH_SECONDS, REGIONS, RSS_FEEDS} from './constants';
import LoginPage from './pages/Login';
import PostsPage from './pages/Posts';
import BlogListPage from './pages/BlogList';
import BlogDetailPage from './pages/BlogDetail';

import type {
  Article,
  EventLabel,
  MineralLabel,
  RegionLabel,
  SentimentLabel,
  SortKey,
  SourceKey,
  SourceStatus,
  TabKey,
  Tags,
  TopicTabKey,
} from './types';

const stripTags = (h: string): string => {
  try {
    const div = document.createElement('div');
    div.innerHTML = h;
    return div.textContent || '';
  } catch {
    return h.replace(/<[^>]*>/g, '');
  }
};

const toCorsSafeImageUrl = (url: string): string => {
  let u = (url || '').trim();
  if (!u) return u;
  if (u.startsWith('data:') || u.startsWith('blob:')) return u;

  // Handle protocol-relative URLs.
  if (u.startsWith('//')) u = `https:${u}`;
  if (!/^https?:\/\//i.test(u)) return u;

  // Use an HTTPS image proxy to avoid hotlink/CORS-related blocks from some publishers.
  // Important: when the origin is HTTPS, tell wsrv to fetch it via `https://...` as well.
  const isHttpsOrigin = /^https:\/\//i.test(u);
  const withoutScheme = u.replace(/^https?:\/\//i, '');
  const wsrvTarget = isHttpsOrigin ? `https://${withoutScheme}` : withoutScheme;
  return `https://images.weserv.nl/?url=${encodeURIComponent(
    wsrvTarget
  )}&default=wsrv.nl/placeholder.svg`;
};

/** App `posts` rows (`type === 1`) use our API image URLs — load directly; others use the CORS-safe proxy. */
const articleThumbSrc = (thumb: string | null | undefined, type?: 0 | 1): string => {
  const u = (thumb || '').trim();
  if (!u) return u;
  if (type === 1) return u;
  return toCorsSafeImageUrl(u);
};

const gdeltDate = (s?: string): string => {
  if (!s || s.length < 15) return new Date().toISOString();
  try {
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}T${s.slice(9, 11)}:${s.slice(11, 13)}:00Z`;
  } catch {
    return new Date().toISOString();
  }
};

const fmtDate = (d: string): string => {
  try {
    const dt = new Date(d);
    const now = new Date();
    const dm = Math.floor((now.getTime() - dt.getTime()) / 60000);
    if (dm < 2) return 'just now';
    if (dm < 60) return `${dm}m ago`;
    if (dm < 1440) return `${Math.floor(dm / 60)}h ago`;
    return dt.toLocaleDateString('en-GB', {day: 'numeric', month: 'short'});
  } catch {
    return 'recently';
  }
};

const sentClass = (s: SentimentLabel): string => {
  return s === 'Bullish' ? 'ts-bull' : s === 'Bearish' ? 'ts-bear' : 'ts-neut';
};

const sentEmj = (s: SentimentLabel): string => {
  return s === 'Bullish' ? '📈' : s === 'Bearish' ? '📉' : '➡️';
};

const tagClass = (mineral: MineralLabel): string => {
  const map: Record<MineralLabel, string> = {
    Lithium: 'tm-li',
    Cobalt: 'tm-co',
    Nickel: 'tm-ni',
    Copper: 'tm-cu',
    REE: 'tm-re',
    Graphite: 'tm-gr',
    Gold: 'tm-au',
    Silver: 'tm-si',
    Zinc: 'tm-zn',
    Manganese: 'tm-mn',
    General: 'tm-gen',
  };
  return map[mineral] || 'tm-gen';
};

const kwTag = (t: string): Tags => {
  const s = t.toLowerCase();
  let mineral: MineralLabel = 'General';
  if (/lithium|li.?ion/.test(s)) mineral = 'Lithium';
  else if (/cobalt/.test(s)) mineral = 'Cobalt';
  else if (/nickel/.test(s)) mineral = 'Nickel';
  else if (/copper/.test(s)) mineral = 'Copper';
  else if (/rare.?earth|ree|neodym|dyspros/.test(s)) mineral = 'REE';
  else if (/graphite|anode.?material/.test(s)) mineral = 'Graphite';
  else if (/\bgold\b/.test(s)) mineral = 'Gold';
  else if (/\bsilver\b/.test(s)) mineral = 'Silver';
  else if (/\bzinc\b/.test(s)) mineral = 'Zinc';
  else if (/manganese/.test(s)) mineral = 'Manganese';

  let event: EventLabel = 'General';
  if (/discov|deposit|reserve|drill|explor/.test(s)) event = 'Discovery';
  else if (/produc|output|smelter/.test(s)) event = 'Production';
  else if (/policy|regulat|permit|ministry/.test(s)) event = 'Policy';
  else if (/acqui|merger|takeover|deal/.test(s)) event = 'M&A';
  else if (/price|cost|\$|spot|market/.test(s)) event = 'Price';
  else if (/invest|fund|financ|billion|million/.test(s)) event = 'Investment';
  else if (/environ|emission|carbon|green/.test(s)) event = 'Environment';
  else if (/tech|innovat|battery|cathode/.test(s)) event = 'Technology';

  let sentiment: SentimentLabel = 'Neutral';
  if (/surge|jump|rise|grow|boom|record|strong|bull|rally/.test(s)) sentiment = 'Bullish';
  else if (/fall|drop|declin|cut|concern|risk|shortage|crisis|weak/.test(s)) sentiment = 'Bearish';

  let region: RegionLabel = 'Global';
  if (/india|delhi|mumbai|rajasthan/.test(s)) region = 'Asia';
  else if (/china|chinese|beijing/.test(s)) region = 'Asia';
  else if (/australia|perth|queensland/.test(s)) region = 'Australia';
  else if (/africa|congo|zambia|zimbabwe|ghana|kenya/.test(s)) region = 'Africa';
  else if (/\bus\b|usa|united states|canada|mexico|brazil|chile/.test(s)) region = 'Americas';
  else if (/europe|eu|germany|france|uk|sweden|finland/.test(s)) region = 'Europe';
  else if (/asia|japan|korea|indonesia|philippines/.test(s)) region = 'Asia';
  else if (/asia|japan|korea|indonesia|philippines|india|delhi|rajasthan|mumbai|china|chinese|beijing/.test(s)) region = 'Asia-Pacific';

  return {mineral, event, sentiment, region};
};

const claudeTag = async (articles: Array<Pick<Article, 'title'>>, apiKey: string): Promise<Tags[]> => {
  const lines = articles.map((a, i) => `${i + 1}. ${a.title}`).join('\n');
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-haiku-4-5-20251001',
      max_tokens: 2000,
      messages: [
        {
          role: 'user',
          content:
            'Classify each headline. Return ONLY a valid JSON array, no markdown.\n' +
            `${lines}\n` +
            'For each: {"mineral":"Lithium|Cobalt|Nickel|Copper|REE|Graphite|Gold|Silver|Zinc|Manganese|General","event":"Discovery|Production|Policy|M&A|Price|Investment|Environment|Technology|General","sentiment":"Bullish|Bearish|Neutral","region":"Asia|Africa|Americas|Europe|Australia|Global"}',
        },
      ],
    }),
  });
  if (!response.ok) throw new Error(`API ${response.status}`);
  const data = (await response.json()) as { content: Array<{ text: string }> };
  return JSON.parse(data.content?.[0]?.text?.replace(/```json|```/g, '').trim() || '[]') as Tags[];
};

function NewsHome() {
  const [session, setSession] = useState<string | null>(() => sessionStorage.getItem(SESSION_KEY));
  const [cfgOpen, setCfgOpen] = useState(false);
  const [apiKey, setApiKey] = useState<string>(() => localStorage.getItem('cm_ak') || '');
  const [akInp, setAkInp] = useState(apiKey);

  const [tab, setTab] = useState<TabKey>('all');
  const [sort, setSort] = useState<SortKey>('latest');
  const [search, setSearch] = useState('');

  const [fMinerals, setFMinerals] = useState<Set<MineralLabel>>(() => new Set());
  const [fRegions, setFRegions] = useState<Set<RegionLabel>>(() => new Set());
  const [fSentiments, setFSentiments] = useState<Set<SentimentLabel>>(() => new Set());

  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sources, setSources] = useState<Record<SourceKey, SourceStatus>>({
    gdelt: {s: 'loading', n: 0},
    'mining.com': {s: 'loading', n: 0},
    'mining_weekly__home': {s: 'loading', n: 0},
    'international_mining': {s: 'loading', n: 0},
    'e_&_mj': {s: 'loading', n: 0},
    canadian_mining_journal: {s: 'loading', n: 0},
  });

  const [countdown, setCountdown] = useState(REFRESH_SECONDS);

  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerLayerRef = useRef<L.LayerGroup | null>(null);
  const didInitialFetchRef = useRef(false);

  const tbDate = useMemo(
    () =>
      new Date().toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    []
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = [...articles];

    const tabFilter = (t: TopicTabKey, a: Article): boolean => {
      const tags = a.tags;
      if (t === 'all') return true;
      if (!tags) return false;
      switch (t) {
        case 'critical':
          return ['Lithium', 'Cobalt', 'Nickel', 'REE', 'Graphite', 'Manganese', 'Zinc', 'Copper'].includes(tags.mineral);
        case 'battery':
          return ['Lithium', 'Cobalt', 'Nickel', 'Graphite', 'Manganese'].includes(tags.mineral);
        case 'gold':
          return ['Gold', 'Silver'].includes(tags.mineral);
        case 'policy':
          return tags.event === 'Policy';
        case 'deals':
          return ['M&A', 'Investment'].includes(tags.event);
        case 'africa':
          return tags.region === 'Africa';
        case 'asia':
          return ['Asia', 'Australia', 'Asia-Pacific'].includes(tags.region);
        case 'bullish':
          return tags.sentiment === 'Bullish';
        default:
          return true;
      }
    };

    arr = arr.filter((a) => tabFilter(tab, a));
    if (q) arr = arr.filter((a) => (a.title || '').toLowerCase().includes(q));
    if (fMinerals.size) arr = arr.filter((a) => a.tags && fMinerals.has(a.tags.mineral));
    if (fRegions.size) arr = arr.filter((a) => a.tags && fRegions.has(a.tags.region));
    if (fSentiments.size) arr = arr.filter((a) => a.tags && fSentiments.has(a.tags.sentiment));

    if (sort === 'bullish') {
      const b = arr.filter((a) => a.tags?.sentiment === 'Bullish');
      arr = [...b, ...arr.filter((a) => a.tags?.sentiment !== 'Bullish')];
    } else if (sort === 'bearish') {
      const b = arr.filter((a) => a.tags?.sentiment === 'Bearish');
      arr = [...b, ...arr.filter((a) => a.tags?.sentiment !== 'Bearish')];
    } else {
      arr.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
    }
    return arr;
  }, [articles, tab, search, fMinerals, fRegions, fSentiments, sort]);

  const hero = filtered[0];
  const gridItems = filtered.slice(1, 1 + PAGE_SZ);
  const hasMore = filtered.length > 1 + PAGE_SZ;
  const listItems = hasMore ? filtered.slice(1 + PAGE_SZ, 1 + PAGE_SZ * (page + 1)) : [];
  const showLoadMore = filtered.length > 1 + PAGE_SZ * (page + 1);

  const activeFilterLabels = useMemo(() => {
    const labels: string[] = [];
    const tabLabels: Record<TopicTabKey, string> = {
      all: 'All News',
      critical: 'Critical Minerals',
      battery: 'Battery Metals',
      gold: 'Gold & Silver',
      policy: 'Policy',
      deals: 'M&A & Deals',
      africa: 'Africa',
      asia: 'Asia-Pacific',
      bullish: 'Bullish',
    };
    if (tab !== 'all') labels.push(tabLabels[tab]);
    if (search.trim()) labels.push(`Search: "${search.trim()}"`);
    fMinerals.forEach((m) => labels.push(m));
    fRegions.forEach((r) => labels.push(r));
    fSentiments.forEach((s) => labels.push(s));
    return labels;
  }, [tab, search, fMinerals, fRegions, fSentiments]);

  const trending = useMemo(() => {
    const cnt: Record<MineralLabel, number> = {
      Lithium: 0,
      Cobalt: 0,
      Nickel: 0,
      Copper: 0,
      REE: 0,
      Graphite: 0,
      Gold: 0,
      Silver: 0,
      Zinc: 0,
      Manganese: 0,
      General: 0,
    };
    articles.forEach((a) => {
      const m = a.tags?.mineral || 'General';
      cnt[m] = (cnt[m] || 0) + 1;
    });
    const sorted = (Object.entries(cnt) as Array<[MineralLabel, number]>)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
    const mx = sorted[0]?.[1] || 1;
    return {sorted, mx};
  }, [articles]);

  const tickerItems = useMemo(() => {
    const items = articles.slice(0, 28).map((a) => ({
      title: a.title,
      mineral: a.tags?.mineral || '',
    }));
    return [...items, ...items];
  }, [articles]);

  const mineralInfo = (label?: MineralLabel) => {
    return MINERALS.find((m) => m.label === (label || 'General')) || MINERALS[10];
  };

  const toggleSet = <T, >(set: Set<T>, val: T): Set<T> => {
    const n = new Set(set);
    if (n.has(val)) n.delete(val);
    else n.add(val);
    return n;
  };

  const fetchGDELT = async (): Promise<Article[]> => {
    const qs = [
      // 'critical minerals OR lithium OR cobalt OR rare earth OR graphite',
      // 'copper mining OR "nickel mine" OR manganese OR battery metals',
      // 'mining industry OR mineral supply OR gold mine OR EV metals',
      '(critical%20minerals%20OR%20lithium%20OR%20cobalt%20OR%20rare%20earth%20OR%20graphite)'
    ];
    const all: Article[] = [];
    for (const q of qs) {
      try {
        const response = await fetch(
          `https://api.gdeltproject.org/api/v2/doc/doc?query=${q}&mode=artlist&maxrecords=25&format=json&sourcelang=eng&timespan=1d`
        );
        const data = (await response.json()) as {
          articles?: Array<{
            title: string;
            url: string;
            socialimage?: string;
            domain?: string;
            sourcecountry?: string;
            seendate?: string;
          }>;
        };
        (data.articles || []).forEach((a) => {
          const thumb = (a.socialimage || '').trim();
          if (!thumb) return; // GDELT: only keep items with a usable thumbnail
          all.push({
            title: a.title,
            url: a.url,
            thumbnail: thumb,
            source: 'gdelt',
            domain: a.domain,
            sourcecountry: (a.sourcecountry || '').toLowerCase(),
            pubDate: gdeltDate(a.seendate),
          });
        });
      } catch {
        // ignore
      }
    }
    setSources((s) => ({...s, gdelt: {s: all.length ? 'ok' : 'error', n: all.length}}));
    return all;
  };

  const fetchRSS = async (): Promise<Article[]> => {
    try {
      const response = await fetch(
        // Proxy RSS fetching/parsing through NestJS backend
        // `https://criticalminerals.news/api/rss/latest`
        FETCH_RSS_URL
      );
      const data = (await response.json()) as {
        data?: Array<{
          title: string;
          link: string;
          pubDate?: string;
          guid?: string;
          thumbnail?: string;
          description?: string;
          source?: string;
          domain?: string;
          type?: 0 | 1;
        }>;
      };
      const items: Article[] = (data.data || []).map((it) => {
        const thumb = (it.thumbnail || '').trim();

        return {
          title: it.title,
          url: it.link,
          thumbnail: thumb ? thumb : null,
          description: it.description ? stripTags(it.description).slice(0, 200) : '',
          source: (it.source || 'mining.com') as SourceKey,
          domain: it.domain || '',
          pubDate: it.pubDate || new Date().toISOString(),
          type: it.type === 1 ? 1 : 0,
        };
      });
      setSources((s) => {
        const next = {...s};
        items.forEach((a) => {
          const k = a.source;
          if (!next[k]) next[k] = {s: 'ok', n: 0};
          next[k] = {s: 'ok', n: (next[k].n || 0) + 1};
        });
        return next;
      });
      return items;
    } catch {
      setSources((s) => {
        const next = {...s};
        (Object.keys(next) as SourceKey[]).forEach((k) => {
          next[k] = {s: 'error', n: 0};
        });
        return next;
      });
      return [];
    }
  };

  const tagAll = async (list: Article[], key: string): Promise<Article[]> => {
    const missing = list.filter((a) => !a.tags);
    if (!missing.length || !key) return list;
    const updated = [...list];
    for (let i = 0; i < missing.length; i += 20) {
      const batch = missing.slice(i, i + 20);
      try {
        const tags = await claudeTag(batch, key);
        batch.forEach((a, j) => {
          const idx = updated.findIndex((x) => x.url === a.url);
          if (idx >= 0) updated[idx] = {...updated[idx], tags: tags[j] || kwTag(a.title || '')};
        });
      } catch {
        batch.forEach((a) => {
          const idx = updated.findIndex((x) => x.url === a.url);
          if (idx >= 0) updated[idx] = {...updated[idx], tags: kwTag(a.title || '')};
        });
      }
    }
    return updated;
  };

  const inFlightRef = useRef<AbortController | null>(null);

  const fetchAll = async (opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;

    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;

    if (!silent) {
      setLoading(true);
      setPage(1);
      setSources({
        gdelt: {s: 'loading', n: 0},
        'mining.com': {s: 'loading', n: 0},
        'mining_weekly__home': {s: 'loading', n: 0},
        'international_mining': {s: 'loading', n: 0},
        'e_&_mj': {s: 'loading', n: 0},
        canadian_mining_journal: {s: 'loading', n: 0},
      });
    }

    try {
      const res = await Promise.allSettled([fetchGDELT(), fetchRSS()]);
      let all: Article[] = [];
      res.forEach((r) => {
        if (r.status === 'fulfilled') all = all.concat(r.value);
      });
      const seen = new Set<string>();
      all = all.filter((a) => {
        if (seen.has(a.url)) return false;
        seen.add(a.url);
        return true;
      });
      all.sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
      all = all.slice(0, 150);

      const baseTagged = all.map((a) => (a.tags ? a : {...a, tags: kwTag(a.title || '')}));
      const final = apiKey ? await tagAll(all, apiKey) : baseTagged;
      if (!controller.signal.aborted) setArticles(final);
    } catch (e) {
      // ignore (including abort)
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const saveCfg = () => {
    const v = akInp.trim();
    setApiKey(v);
    localStorage.setItem('cm_ak', v);
    setCfgOpen(false);
  };

  useEffect(() => {
    if (didInitialFetchRef.current) return;
    didInitialFetchRef.current = true;
    fetchAll({silent: false});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setCountdown(REFRESH_SECONDS);
    const id = window.setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          fetchAll({silent: true});
          return REFRESH_SECONDS;
        }
        return c - 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: false,
    }).setView([20, 10], 1);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      subdomains: 'abcd',
    }).addTo(map);
    const layer = L.layerGroup().addTo(map);
    mapRef.current = map;
    markerLayerRef.current = layer;
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const layer = markerLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    const cc: Record<string, number> = {};
    articles.forEach((a) => {
      const k = (a.sourcecountry || '').toLowerCase();
      if (k) cc[k] = (cc[k] || 0) + 1;
    });

    Object.entries(cc).forEach(([c, n]) => {
      const ll =
        COUNTRY_LL[c] ||
        Object.entries(COUNTRY_LL).find(([k]) => c.includes(k))?.[1];
      if (!ll) return;
      L.circleMarker(ll, {
        radius: Math.min(3 + n * 1.5, 11),
        fillColor: '#2563eb',
        color: '#2563eb',
        weight: 1,
        opacity: 0.9,
        fillOpacity: 0.45,
      }).addTo(layer);
    });
  }, [articles]);

  // const aiLabel = apiKey ? '✓ AI Active' : '⚙ AI Tagging';

  const countdownText = `${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, '0')}`;

  return (
    <div>
      <div className="topbar">
        <span className="tb-date">{tbDate}</span>
        <span style={{color: '#333'}}>|</span>
        <span className="live-pill">
          <span className="live-dot"/>
          LIVE
        </span>
        <div className="tb-right">
          {session ? (
            <>
              <Link to="/posts" className="tb-link">
                Posts
              </Link>
              <span className="tb-sep">|</span>
              <button
                type="button"
                className="tb-link"
                onClick={() => {
                  sessionStorage.removeItem(SESSION_KEY);
                  setSession(null);
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <Link to="/login" className="tb-link">
              Log in
            </Link>
          )}
          <span className="tb-sep">|</span>
          <Link to="/blog" className="tb-link">
            Blog
          </Link>
          <span className="tb-sep">|</span>
          <button
            type="button"
            className={`tb-link ${apiKey ? 'on' : ''}`}
            onClick={() => setCfgOpen((o) => !o)}
            style={apiKey ? {color: '#6ee7b7'} : undefined}
          >
          </button>
          <span className="tb-sep">|</span>
          <button type="button" className="tb-link" onClick={() => fetchAll({silent: false})}>
            Refresh
          </button>
        </div>
      </div>

      <div className={`cfg-bar ${cfgOpen ? 'open' : ''}`}>
        <label>Anthropic API Key</label>
        <input
          type="password"
          value={akInp}
          onChange={(e) => setAkInp(e.target.value)}
          placeholder="sk-ant-api03-… (used for AI tagging, stored locally only)"
        />
        <button type="button" className="cfg-save" onClick={saveCfg}>
          Save
        </button>
        <button type="button" className="cfg-close" onClick={() => setCfgOpen(false)}>
          Close
        </button>
      </div>

      <div className="ticker">
        <div className="ticker-lbl">Breaking</div>
        <div className="ticker-track">
          <div className="ticker-inner">
            {tickerItems.length ? (
              tickerItems.map((it, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <span className="t-item" key={`${it.title}-${i}`}>
                  {it.mineral ? <span className="t-tag">{it.mineral}</span> : null}
                  {it.title}
                </span>
              ))
            ) : (
              <span className="t-item">Loading live mineral intelligence…</span>
            )}
          </div>
        </div>
      </div>

      <div className="masthead">
        <Link className="brand" to="/">
          <div className="brand-name">
            Critical<em>Minerals</em>.News
          </div>
          <div className="brand-sub">Global Mining &amp; Minerals Intelligence Platform</div>
        </Link>
        <div className="mh-right">
          <div className="mh-stats">
            <span>
              Articles: <strong>{articles.length ? articles.length : '—'}</strong>
            </span>
            <span>
              Sources: <strong>{RSS_FEEDS.length} Live</strong>
            </span>
            <span>
              Next refresh: <strong style={{color: 'var(--gold)'}}>{countdownText}</strong>
            </span>
          </div>
          <div className="mh-btns">
            <button type="button" className="mh-btn outline" onClick={() => setCfgOpen((o) => !o)}>
              ⚙ Settings
            </button>
            <button type="button" className="mh-btn" onClick={() => fetchAll({silent: false})}>
              ↻ Refresh Now
            </button>
          </div>
        </div>
      </div>

      <nav>
        <div className="nav-inner">
          {([
            {k: 'all', label: '🌐 All News'},
            {k: 'critical', label: '⚡ Critical Minerals'},
            {k: 'battery', label: '🔋 Battery Metals'},
            {k: 'gold', label: '🟡 Gold & Silver'},
            {k: 'policy', label: '📋 Policy'},
            {k: 'deals', label: '🤝 M&A & Deals'},
            {k: 'africa', label: '🌍 Africa'},
            {k: 'asia', label: '🌏 Asia-Pacific'},
            {k: 'bullish', label: '📈 Bullish'},
          ] as Array<{ k: TopicTabKey; label: string }>).map((t) => (
            <button
              key={t.k}
              type="button"
              className={`nav-item ${tab === t.k ? 'active' : ''}`}
              onClick={() => {
                setTab(t.k);
                setPage(1);
              }}
            >
              {t.label}
            </button>
          ))}

          <div className="nav-right">
            <input
              className="nav-input"
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="🔎 Search headlines…"
            />
          </div>
        </div>
      </nav>

      <div className="ad-lb-wrap">
        <a href="https://www.geoexpore.com" target="_blank" rel="noopener noreferrer sponsored">
          <div className="ad-geo-lb">
            <span className="ad-lb-lbl">Advertisement</span>
            <div className="ad-geo-wild">
              <div className="ad-geo-brand-wrap">
                <svg className="ad-sbar-logo" width="71" height="48" viewBox="0 0 71 48" fill="#0A529E"
                     aria-hidden="true">
                  <g id="surface1">
                    <path
                      d="M 55.136719 5.699219 C 52.820312 8.101562 49.839844 10.765625 48.582031 11.632812 L 46.261719 13.199219 L 46.890625 14.832031 C 47.257812 15.765625 47.585938 16.566406 47.652344 16.632812 C 48.050781 17.234375 62.421875 9.898438 63.78125 8.367188 C 64.144531 7.964844 64.476562 6.964844 64.542969 6.101562 C 64.808594 3.464844 62.953125 1.332031 60.4375 1.332031 C 59.507812 1.332031 58.847656 1.898438 55.136719 5.699219 Z M 55.136719 5.699219 "/>
                    <path
                      d="M 17.617188 3.332031 C 9.207031 5.601562 3.078125 12.199219 1.191406 21 C -0.398438 28.433594 1.820312 37.167969 6.492188 41.867188 C 14.605469 50 28.148438 47.867188 31.757812 37.867188 C 34.109375 31.265625 31.691406 24.035156 26.0625 21.066406 C 24.703125 20.367188 23.84375 20.167969 21.195312 20.066406 L 17.949219 19.933594 L 16.789062 21.101562 C 15.828125 22.066406 15.628906 22.5 15.628906 23.734375 C 15.628906 26.332031 16.957031 27.433594 20.167969 27.5 C 21.691406 27.5 22.484375 27.699219 23.148438 28.199219 C 25.832031 30.199219 25.894531 34.734375 23.28125 37.367188 C 20.132812 40.535156 14.074219 39.632812 10.960938 35.535156 C 9.503906 33.601562 8.675781 31.300781 8.3125 28.234375 C 7.449219 20.332031 11.589844 13.300781 18.511719 10.964844 C 20.367188 10.367188 21.195312 10.265625 24.375 10.398438 C 30.035156 10.667969 33.28125 12.234375 35.863281 15.898438 C 37.488281 18.265625 38.382812 21.199219 38.976562 26.234375 C 40.796875 41.464844 43.910156 46.398438 51.726562 46.433594 C 54.804688 46.433594 56.957031 45.867188 63.382812 43.433594 C 69.3125 41.132812 70.269531 40.398438 70.269531 38.101562 C 70.269531 36.832031 70.105469 36.433594 69.144531 35.464844 C 67.324219 33.632812 66.464844 33.800781 60.269531 37.398438 C 55.832031 39.964844 54.738281 40.464844 53.21875 40.601562 C 49.109375 40.933594 47.652344 38.035156 46.625 27.132812 C 45.734375 18 43.84375 13.066406 39.640625 8.800781 C 35.664062 4.765625 31.226562 3.035156 24.242188 2.800781 C 20.828125 2.699219 19.671875 2.765625 17.617188 3.332031 Z M 17.617188 3.332031 "/>
                    <path
                      d="M 60.136719 19.332031 C 56.894531 22.332031 53.414062 24.933594 50.933594 26.234375 L 49.34375 27.035156 L 49.574219 28.933594 C 49.707031 29.964844 49.839844 30.867188 49.90625 30.933594 C 50.136719 31.167969 56.992188 28.699219 61.5625 26.765625 C 67.65625 24.167969 68.117188 23.699219 67.988281 20.234375 C 67.921875 18.800781 66.097656 17 64.441406 16.765625 C 63.117188 16.601562 63.019531 16.667969 60.136719 19.332031 Z M 60.136719 19.332031 "/>
                  </g>
                </svg>
                <span className="ad-geo-redline"/>
                <span className="ad-geo-span" style={{color: '#0C5392', textDecoration: 'none'}}>GeoExpOre Pvt.Ltd</span>
              </div>
              <div className="ad-geo-wild-content">
                <span className="ad-geo-wild-title">Mineral Exploration & Processing</span>
                <span className="ad-geo-wild-label">Geophysical &middot; Geochemical &middot; Airborne &middot; Mineral Processing &middot; Tailings Dam</span>
                {/*<span className="ad-geo-wild-label">Accredited: QCI-NABET | Ministry of Mines, Govt of India</span>*/}
              </div>
              <span className="ad-wide-geo-cta">www.GeoExpOre.Com →</span>
            </div>
          </div>
        </a>
      </div>

      <div className="wrapper">
        <main>
          {loading ? (
            <div className="hero-row" style={{pointerEvents: 'none'}}>
              <div className="hero-img">
                <div className="sk" style={{width: '100%', minHeight: 280}}/>
              </div>
              <div className="hero-body">
                <div className="sk" style={{height: 10, width: 150, marginBottom: 14}}/>
                <div className="sk" style={{height: 22, width: '100%', marginBottom: 7}}/>
                <div className="sk" style={{height: 22, width: '80%', marginBottom: 16}}/>
                <div className="sk" style={{height: 12, width: '100%', marginBottom: 5}}/>
                <div className="sk" style={{height: 12, width: '70%'}}/>
              </div>
            </div>
          ) : hero ? (
            (() => {
              const t = hero.tags || kwTag(hero.title || '');
              const mi = mineralInfo(t.mineral);
              return (
                <div className="hero-row">
                  <div className="hero-img">
                    <div className="hero-ph">{mi.icon}</div>
                    {hero.thumbnail ? (
                      <img
                        src={articleThumbSrc(hero.thumbnail, hero.type)}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        {...(hero.type !== 1 ? {crossOrigin: 'anonymous' as const} : {})}
                        style={{position: 'absolute', top: 0, left: 0}}
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (hero.type === 1) {
                            el.style.display = 'none';
                            return;
                          }
                          if (!el.dataset.fallbackTried) {
                            el.dataset.fallbackTried = '1';
                            el.src = hero.thumbnail || '';
                            return;
                          }
                          el.style.display = 'none';
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="hero-body" style={{borderLeftColor: mi.color}}>
                    <div>
                      <div className="hero-tags">
                        <span className={`tag ${tagClass(t.mineral)}`}>{t.mineral}</span>
                        <span className="tag te">{t.event}</span>
                        <span className={`tag ${sentClass(t.sentiment)}`}>
                          {sentEmj(t.sentiment)} {t.sentiment}
                        </span>
                        <span className="tag te">{t.region}</span>
                      </div>
                      <a href={hero.url} target="_blank" rel="noopener noreferrer"
                         className="hero-hl">
                        {hero.title}
                      </a>
                      {hero.description ? (
                        <p className="hero-desc">{hero.description.slice(0, 230)}…</p>
                      ) : (
                        <p className="hero-desc"
                           style={{color: 'var(--text3)', fontStyle: 'italic'}}>
                          Click to read full article on source website.
                        </p>
                      )}
                    </div>
                    <div className="hero-meta">
                      <span className="hero-src">{hero.domain || hero.source}</span>
                      <span>·</span>
                      <span>{fmtDate(hero.pubDate)}</span>
                      {apiKey ? (
                        <span style={{marginLeft: 'auto'}}>
                          <span className="ai-badge">✦ AI Tagged</span>
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            })()
          ) : null}

          <div className={`filter-bar ${activeFilterLabels.length ? 'show' : ''}`}>
            {activeFilterLabels.map((t) => (
              <span key={t} className="filter-active-tag">
                {t}
              </span>
            ))}
            {activeFilterLabels.length ? (
              <button
                type="button"
                className="filter-clear"
                onClick={() => {
                  setTab('all');
                  setSearch('');
                  setFMinerals(new Set());
                  setFRegions(new Set());
                  setFSentiments(new Set());
                  setPage(1);
                }}
              >
                Clear filters
              </button>
            ) : null}
          </div>

          <div className="sec-hdr blue">
            <span className="sec-title">Latest Stories</span>
            <span className="sec-pill blue">{filtered.length ? `${filtered.length} stories` : '—'}</span>
            <div className="sec-rule"/>
            <select
              className="sec-sort"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
            >
              <option value="latest">Newest first</option>
              <option value="bullish">Bullish first</option>
              <option value="bearish">Bearish first</option>
            </select>
          </div>

          <div className="news-grid">
            {loading
              ? Array.from({length: 6}).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className="card" key={i} style={{pointerEvents: 'none'}}>
                  <div className="card-img">
                    <div className="sk" style={{width: '100%', height: 138, borderRadius: 0}}/>
                  </div>
                  <div className="card-body">
                    <div className="sk" style={{height: 9, width: 110, marginBottom: 9}}/>
                    <div className="sk" style={{height: 15, width: '100%', marginBottom: 5}}/>
                    <div className="sk" style={{height: 15, width: '78%', marginBottom: 9}}/>
                    <div className="sk" style={{height: 9, width: 90}}/>
                  </div>
                </div>
              ))
              : (() => {
                const cards = gridItems.map((a, i) => {
                  const t = a.tags || kwTag(a.title || '');
                  const mi = mineralInfo(t.mineral);
                  return (
                    <div
                      className="card"
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${a.url}-${i}`}
                      style={{
                        ['--card-color' as unknown as string]: mi.color,
                        animationDelay: `${i * 35}ms`
                      } as React.CSSProperties}
                      role="button"
                      tabIndex={0}
                      onClick={() => window.open(a.url, '_blank', 'noopener,noreferrer')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') window.open(a.url, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      <div className="card-img">
                        <div className="card-ph">{mi.icon}</div>
                        {a.thumbnail ? (
                          <img
                            src={articleThumbSrc(a.thumbnail, a.type)}
                            alt=""
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            {...(a.type !== 1 ? {crossOrigin: 'anonymous' as const} : {})}
                            style={{position: 'absolute', top: 0, left: 0}}
                            onError={(e) => {
                              const el = e.currentTarget;
                              if (a.type === 1) {
                                el.style.display = 'none';
                                return;
                              }
                              if (!el.dataset.fallbackTried) {
                                el.dataset.fallbackTried = '1';
                                el.src = a.thumbnail || '';
                                return;
                              }
                              el.style.display = 'none';
                            }}
                          />
                        ) : null}
                      </div>
                      <div className="card-body">
                        <div className="card-tags">
                          <span className={`tag ${tagClass(t.mineral)}`}>{t.mineral}</span>
                          <span className={`tag ${sentClass(t.sentiment)}`}>
                              {sentEmj(t.sentiment)} {t.sentiment}
                            </span>
                        </div>
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="card-hl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {a.title}
                        </a>
                        {a.description ? <div
                          className="card-desc">{a.description.slice(0, 105)}…</div> : null}
                        <div className="card-meta">
                            <span className="card-src" style={{color: mi.color}}>
                              {a.domain || a.source}
                            </span>
                          <span>·</span>
                          <span>{fmtDate(a.pubDate)}</span>
                        </div>
                      </div>
                    </div>
                  );
                });

                if (cards.length >= 4) {
                  cards.splice(
                    4,
                    0,
                    <a className="ad-native" href="https://earthscience.ai" target="_blank"
                       rel="noopener noreferrer sponsored" key="native-ad">
                      <div className="ad-native-flag">AD</div>
                      <div className="ad-native-img">🌍</div>
                      <div className="ad-native-body">
                        <div className="ad-native-eyebrow">★ Sponsored · EarthScience.AI</div>
                        <div className="ad-native-hl">
                          Advanced Critical Minerals Intelligence — Digital Twins,
                          Maps &amp; AI Analytics
                        </div>
                        <div className="ad-native-sub">
                          Explore 34+ minerals across 75+ countries with interactive 3D
                          Digital Twin dashboards.
                        </div>
                        <div className="ad-native-cta">🔗 Visit EarthScience.AI →</div>
                        <div className="ad-native-meta">Sponsored Content</div>
                      </div>
                    </a>
                  );
                }
                return cards;
              })()}

            {!loading && !filtered.length ? (
              <div className="empty" style={{gridColumn: '1 / -1'}}>
                <div className="empty-icon">⛏</div>
                <div className="empty-msg">No stories match your filters</div>
                <div className="empty-sub">Try adjusting your selection or wait for the next refresh.
                </div>
              </div>
            ) : null}
          </div>

          <div className="sec-hdr purple" style={{display: hasMore ? undefined : 'none'}}>
            <span className="sec-title">More Stories</span>
            <div className="sec-rule"/>
          </div>

          <div>
            {listItems.map((a, i) => {
              const t = a.tags || kwTag(a.title || '');
              const mi = mineralInfo(t.mineral);
              return (
                <a
                  className="lcard"
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${a.url}-l-${i}`}
                  style={{animationDelay: `${i * 28}ms`}}
                >
                  <div className="lcard-thumb" style={{position: 'relative'}}>
                    <div className="lcard-ph">{mi.icon}</div>
                    {a.thumbnail ? (
                      <img
                        src={articleThumbSrc(a.thumbnail, a.type)}
                        alt=""
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        {...(a.type !== 1 ? {crossOrigin: 'anonymous' as const} : {})}
                        style={{position: 'absolute', top: 0, left: 0}}
                        onError={(e) => {
                          const el = e.currentTarget;
                          if (a.type === 1) {
                            el.style.display = 'none';
                            return;
                          }
                          if (!el.dataset.fallbackTried) {
                            el.dataset.fallbackTried = '1';
                            el.src = a.thumbnail || '';
                            return;
                          }
                          el.style.display = 'none';
                        }}
                      />
                    ) : null}
                  </div>
                  <div>
                    <div style={{display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 2}}>
                      <span className={`tag ${tagClass(t.mineral)}`}>{t.mineral}</span>
                      <span className={`tag ${sentClass(t.sentiment)}`}>
                        {sentEmj(t.sentiment)} {t.sentiment}
                      </span>
                    </div>
                    <div className="lcard-hl">{a.title}</div>
                    <div className="lcard-meta">
                      <span className="lcard-src" style={{color: mi.color}}>
                        {a.domain || a.source}
                      </span>
                      <span>·</span>
                      <span>{fmtDate(a.pubDate)}</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>

          <button
            type="button"
            className="load-more"
            style={{display: showLoadMore ? undefined : 'none'}}
            onClick={() => setPage((p) => p + 1)}
          >
            Load more stories ↓
          </button>
        </main>

        <aside className="sidebar">
          <div className="spanel">
            <div className="spanel-hdr blue">⚡ Filter by Mineral</div>
            <div className="spanel-body" style={{display: 'flex', flexWrap: 'wrap', gap: 5}}>
              {MINERALS.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={`chip ${m.cc} ${fMinerals.has(m.label) ? 'on' : ''}`}
                  onClick={() => {
                    setFMinerals((s) => toggleSet(s, m.label));
                    setPage(1);
                  }}
                >
                  {m.icon} {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="spanel">
            <div className="spanel-hdr green">🌎 Region &amp; Sentiment</div>
            <div className="spanel-body">
              <div
                style={{
                  fontSize: 9,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: 'var(--text3)',
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                Region
              </div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 12}}>
                {REGIONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className={`chip chip-r ${fRegions.has(r) ? 'on' : ''}`}
                    onClick={() => {
                      setFRegions((s) => toggleSet(s, r));
                      setPage(1);
                    }}
                  >
                    {r}
                  </button>
                ))}
              </div>

              <div
                style={{
                  fontSize: 9,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  color: 'var(--text3)',
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                Sentiment
              </div>
              <div style={{display: 'flex', gap: 5}}>
                {([
                  {k: 'Bullish', e: '📈'},
                  {k: 'Bearish', e: '📉'},
                  {k: 'Neutral', e: '➡️'},
                ] as const).map((x) => (
                  <button
                    key={x.k}
                    type="button"
                    className={`chip ${fSentiments.has(x.k) ? 'on' : ''}`}
                    style={{color: 'var(--text2)'}}
                    onClick={() => {
                      setFSentiments((s) => toggleSet(s, x.k));
                      setPage(1);
                    }}
                  >
                    {x.e} {x.k}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="spanel">
            <div className="spanel-hdr red">🔥 Trending Minerals</div>
            <div className="spanel-body">
              {trending.sorted.map(([m, n], i) => {
                const mi = mineralInfo(m);
                const w = `${Math.round((n / trending.mx) * 100)}%`;
                return (
                  <button
                    key={m}
                    type="button"
                    className="trend-item"
                    onClick={() => {
                      setFMinerals(new Set([m]));
                      setPage(1);
                      window.scrollTo({top: 0, behavior: 'smooth'});
                    }}
                  >
                    <span className="trend-n">{i + 1}</span>
                    <div style={{flex: 1}}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: 3,
                        }}
                      >
                        <span className="trend-name" style={{color: mi.color}}>
                          {mi.icon} {m}
                        </span>
                        <span className="trend-cnt">{n}</span>
                      </div>
                      <div className="trend-bw">
                        <div className="trend-bf" style={{width: w, background: mi.color}}/>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="spanel">
            <div className="spanel-hdr amber">💵 Market Snapshot</div>
            <div className="spanel-body">
              <div className="price-row">
                <span className="price-m">Copper</span>
                <span className="price-v">$9,340/t</span>
                <span className="price-c pu">+0.8%</span>
              </div>
              <div className="price-row">
                <span className="price-m">Lithium Carb.</span>
                <span className="price-v">$12,200/t</span>
                <span className="price-c pd">-1.2%</span>
              </div>
              <div className="price-row">
                <span className="price-m">Cobalt</span>
                <span className="price-v">$28,500/t</span>
                <span className="price-c pu">+0.3%</span>
              </div>
              <div className="price-row">
                <span className="price-m">Nickel</span>
                <span className="price-v">$15,800/t</span>
                <span className="price-c pd">-0.5%</span>
              </div>
              <div className="price-row">
                <span className="price-m">Gold (oz)</span>
                <span className="price-v">$2,182</span>
                <span className="price-c pu">+0.4%</span>
              </div>
              <div className="price-row">
                <span className="price-m">REE (Nd)</span>
                <span className="price-v">$73,000/t</span>
                <span className="price-c pu">+1.1%</span>
              </div>
            </div>
          </div>

          <div className="ad-slot">
            <div className="ad-slot-label">Advertisement</div>
            <div className="ad-sbar-wrap">
              <div className="ad-sbar-lbl">Sponsored</div>
              <a href="https://earthscience.ai" target="_blank" rel="noopener noreferrer sponsored"
                 style={{textDecoration: 'none', display: 'block'}}>
                <div className="ad-sbar-inner">
                  <svg className="ad-sbar-logo" width="44" height="44" viewBox="0 0 44 44" fill="none"
                       aria-hidden="true">
                    <polygon points="22,3 41,12.5 41,31.5 22,41 3,31.5 3,12.5" fill="none"
                             stroke="#3b82f6" strokeWidth="1.5"/>
                    <polygon
                      points="22,10 34,17 34,27 22,34 10,27 10,17"
                      fill="rgba(59,130,246,.1)"
                      stroke="#3b82f6"
                      strokeWidth=".7"
                      strokeDasharray="2,2"
                    />
                    <circle cx="22" cy="22" r="5" fill="#3b82f6" opacity=".9"/>
                    <circle cx="22" cy="22" r="9" fill="none" stroke="#3b82f6" strokeWidth=".5"
                            opacity=".3"/>
                    <line x1="22" y1="3" x2="22" y2="10" stroke="#3b82f6" strokeWidth=".8"
                          opacity=".4"/>
                    <line x1="41" y1="12.5" x2="34" y2="17" stroke="#3b82f6" strokeWidth=".8"
                          opacity=".4"/>
                    <line x1="41" y1="31.5" x2="34" y2="27" stroke="#3b82f6" strokeWidth=".8"
                          opacity=".4"/>
                  </svg>
                  <div className="ad-sbar-name">
                    <span>EarthScience</span>.AI
                  </div>
                  <div className="ad-sbar-tagline">
                    Critical Minerals Intelligence
                    <br/>
                    Platform for Geoscientists &amp; Investors
                  </div>
                  <div className="ad-sbar-pills">
                    <span className="ad-sbar-pill">3D Digital Twins</span>
                    <span className="ad-sbar-pill">34+ Minerals</span>
                    <span className="ad-sbar-pill">75+ Countries</span>
                    <span className="ad-sbar-pill">AI Analytics</span>
                  </div>
                  <span className="ad-sbar-cta">Explore Platform →</span>
                </div>
              </a>
            </div>
          </div>

          <div className="spanel">
            <div className="spanel-hdr dark">📡 Data Sources</div>
            <div className="spanel-body">
              {([
                {k: 'gdelt', l: 'GDELT Global'},
                {k: 'mining.com', l: 'Mining.com'},
                {k: 'mining_weekly__home', l: 'Mining Weekly'},
                {k: 'international_mining', l: "Int'l Mining"},
                {k: 'e_&_mj', l: 'E&MJ'},
                {k: 'canadian_mining_journal', l: 'Canadian MJ'},
              ] as const).map((r) => {
                const st = sources[r.k];
                const dot = st.s === 'ok' ? 'dok' : st.s === 'error' ? 'derr' : 'dload';
                return (
                  <div className="src-row" key={r.k}>
                    <div className={`sdot ${dot}`}/>
                    <span className="src-n">{r.l}</span>
                    <span className="src-c">{st.n}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="spanel">
            <div className="spanel-hdr dark">🗺 News Origins</div>
            <div id="newsMap" ref={mapEl}/>
          </div>
          <div className="ad-slot">
            <div className="ad-slot-label">Advertisement</div>
            <div className="ad-sbar-wrap">
              <div className="ad-sbar-lbl">Sponsored</div>
              <a href="https://www.geoexpore.com" target="_blank" rel="noopener noreferrer sponsored"
                 style={{textDecoration: 'none', display: 'block'}}>
                <div className="ad-sbar-inner" style={{background: "#fff"}}>
                  <div className="ad-geo-brand-wrap">
                    <svg className="ad-sbar-logo" width="71" height="48" viewBox="0 0 71 48" fill="#0A529E"
                         aria-hidden="true">
                      <g id="surface1">
                        <path
                          d="M 55.136719 5.699219 C 52.820312 8.101562 49.839844 10.765625 48.582031 11.632812 L 46.261719 13.199219 L 46.890625 14.832031 C 47.257812 15.765625 47.585938 16.566406 47.652344 16.632812 C 48.050781 17.234375 62.421875 9.898438 63.78125 8.367188 C 64.144531 7.964844 64.476562 6.964844 64.542969 6.101562 C 64.808594 3.464844 62.953125 1.332031 60.4375 1.332031 C 59.507812 1.332031 58.847656 1.898438 55.136719 5.699219 Z M 55.136719 5.699219 "/>
                        <path
                          d="M 17.617188 3.332031 C 9.207031 5.601562 3.078125 12.199219 1.191406 21 C -0.398438 28.433594 1.820312 37.167969 6.492188 41.867188 C 14.605469 50 28.148438 47.867188 31.757812 37.867188 C 34.109375 31.265625 31.691406 24.035156 26.0625 21.066406 C 24.703125 20.367188 23.84375 20.167969 21.195312 20.066406 L 17.949219 19.933594 L 16.789062 21.101562 C 15.828125 22.066406 15.628906 22.5 15.628906 23.734375 C 15.628906 26.332031 16.957031 27.433594 20.167969 27.5 C 21.691406 27.5 22.484375 27.699219 23.148438 28.199219 C 25.832031 30.199219 25.894531 34.734375 23.28125 37.367188 C 20.132812 40.535156 14.074219 39.632812 10.960938 35.535156 C 9.503906 33.601562 8.675781 31.300781 8.3125 28.234375 C 7.449219 20.332031 11.589844 13.300781 18.511719 10.964844 C 20.367188 10.367188 21.195312 10.265625 24.375 10.398438 C 30.035156 10.667969 33.28125 12.234375 35.863281 15.898438 C 37.488281 18.265625 38.382812 21.199219 38.976562 26.234375 C 40.796875 41.464844 43.910156 46.398438 51.726562 46.433594 C 54.804688 46.433594 56.957031 45.867188 63.382812 43.433594 C 69.3125 41.132812 70.269531 40.398438 70.269531 38.101562 C 70.269531 36.832031 70.105469 36.433594 69.144531 35.464844 C 67.324219 33.632812 66.464844 33.800781 60.269531 37.398438 C 55.832031 39.964844 54.738281 40.464844 53.21875 40.601562 C 49.109375 40.933594 47.652344 38.035156 46.625 27.132812 C 45.734375 18 43.84375 13.066406 39.640625 8.800781 C 35.664062 4.765625 31.226562 3.035156 24.242188 2.800781 C 20.828125 2.699219 19.671875 2.765625 17.617188 3.332031 Z M 17.617188 3.332031 "/>
                        <path
                          d="M 60.136719 19.332031 C 56.894531 22.332031 53.414062 24.933594 50.933594 26.234375 L 49.34375 27.035156 L 49.574219 28.933594 C 49.707031 29.964844 49.839844 30.867188 49.90625 30.933594 C 50.136719 31.167969 56.992188 28.699219 61.5625 26.765625 C 67.65625 24.167969 68.117188 23.699219 67.988281 20.234375 C 67.921875 18.800781 66.097656 17 64.441406 16.765625 C 63.117188 16.601562 63.019531 16.667969 60.136719 19.332031 Z M 60.136719 19.332031 "/>
                      </g>
                    </svg>
                    <span className="ad-geo-redline"/>
                    <span className="ad-geo-span" style={{color: '#0C5392'}}>GeoExpOre Pvt.Ltd</span>
                  </div>
                  <div className="ad-geo-grey-line"></div>
                  <div className="ad-geo-headline">
                    GeoExpOre Pvt. Ltd
                  </div>
                  <div className="ad-geo-tagline">
                    Minerals can't be grown!
                  </div>
                  <div className="ad-geo-grey-line"></div>
                  <div className="ad-geo-pills">
                    <span className="ad-geo-pill">Geophysical & Geochemical Surveys</span>
                    <span className="ad-geo-pill">Airborne & Drone Surveys</span>
                    <span className="ad-geo-pill">Mineral Processing</span>
                    <span className="ad-geo-pill">Tailings Dam Expertise</span>
                  </div>
                  <div className="ad-geo-grey-line"></div>
                  <span className="ad-geo-span-2">Accredited: QCI-NABET | Ministry of Mines, Gol</span>
                  <span className="ad-sbar-cta ad-geo-cta">www.GeoExpOre.Com →</span>
                </div>
              </a>
            </div>
          </div>
        </aside>
      </div>

      <div className="ad-slot" style={{
        background: 'var(--paper2)',
        borderTop: '1px solid var(--rule)',
        borderBottom: '1px solid var(--rule)',
        padding: '6px 0 20px'
      }}>
        <div className="ad-slot-label" style={{paddingTop: 8, fontSize: 9, color: '#aaa'}}>
          ■ ADVERTISEMENT ■
        </div>
        <div className="ad-billboard-wrap">
          <div className="ad-billboard">
            <a href="https://earthscience.ai" target="_blank" rel="noopener noreferrer sponsored"
               style={{textDecoration: 'none', display: 'block'}}>
              <div className="ad-bb-inner">
                <div className="ad-bb-visual">
                  <div className="ad-bb-hexwrap">
                    <svg className="ad-bb-hex" width="120" height="120" viewBox="0 0 120 120"
                         fill="none" aria-hidden="true">
                      <polygon points="60,6 110,33 110,87 60,114 10,87 10,33" fill="none"
                               stroke="rgba(59,130,246,.4)" strokeWidth="1.5"/>
                      <polygon points="60,20 96,41 96,79 60,100 24,79 24,41"
                               fill="rgba(59,130,246,.07)" stroke="rgba(59,130,246,.25)"
                               strokeWidth="1"/>
                      <polygon
                        points="60,34 82,47 82,73 60,86 38,73 38,47"
                        fill="rgba(59,130,246,.05)"
                        stroke="rgba(59,130,246,.15)"
                        strokeWidth=".8"
                        strokeDasharray="3,2"
                      />
                      <circle cx="60" cy="60" r="12" fill="#2563eb" opacity=".85"/>
                      <circle cx="60" cy="60" r="22" fill="none" stroke="#3b82f6" strokeWidth=".7"
                              opacity=".4"/>
                      <circle cx="60" cy="60" r="32" fill="none" stroke="#3b82f6" strokeWidth=".5"
                              opacity=".2"/>
                      <circle cx="60" cy="28" r="3" fill="#60a5fa" opacity=".7"/>
                      <circle cx="92" cy="78" r="2.5" fill="#34d399" opacity=".7"/>
                      <circle cx="28" cy="78" r="2.5" fill="#f59e0b" opacity=".7"/>
                    </svg>
                  </div>
                </div>
                <div className="ad-bb-content">
                  <div className="ad-bb-eyebrow">Geoscience Intelligence Platform</div>
                  <div className="ad-bb-hl">
                    <em>EarthScience</em>
                    <span className="dot">.</span>AI
                  </div>
                  <div className="ad-bb-desc">
                    The world&apos;s most advanced critical minerals intelligence platform. Explore
                    3D Digital Twin dashboards, interactive geological maps, and AI-powered
                    analytics across 34+ minerals and 75+ countries.
                  </div>
                  <div className="ad-bb-pills">
                    <span className="ad-bb-pill pill-blue">⚡ 3D Digital Twins</span>
                    <span className="ad-bb-pill pill-green">🌎 75+ Countries</span>
                    <span className="ad-bb-pill pill-purple">💎 34+ Minerals</span>
                    <span className="ad-bb-pill pill-amber">🤖 AI Analytics</span>
                  </div>
                  <div className="ad-bb-actions">
                    <span className="ad-bb-cta">Explore EarthScience.AI →</span>
                    <span className="ad-bb-url">www.earthscience.ai</span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <footer className="cm-footer">
        <div className="cm-footer-inner">
          <div>
            <div className="cm-footer-brand">
              Critical<span className="cm-footer-brand-accent">Minerals</span>.News
            </div>
            <div className="cm-footer-sub">
              A product of{' '}
              <a href="https://earthscience.ai" target="_blank" rel="noopener noreferrer"
                 className="cm-footer-link-strong">
                Neurons AI Pvt Ltd
              </a>{' '}
              &nbsp;·&nbsp; Bengaluru, India
            </div>
          </div>
          <div className="cm-footer-desc">
            CriticalMinerals.News is a news aggregation service. All articles link to their original
            publishers. Content sourced from
            publicly available global news feeds. We do not claim ownership of third-party content.
          </div>
          <div className="cm-footer-right">
            <div className="cm-footer-links">
              <button type="button" className="cm-footer-link cm-footer-link-btn" onClick={() => {
              }}>
                Privacy Policy
              </button>
              <button type="button" className="cm-footer-link cm-footer-link-btn" onClick={() => {
              }}>
                Terms of Use
              </button>
              <button type="button" className="cm-footer-link cm-footer-link-btn" onClick={() => {
              }}>
                Disclaimer
              </button>
              <a href="mailto:hello@neuronsai.in" className="cm-footer-link">
                Contact
              </a>
            </div>
            <div className="cm-footer-copy">© 2025 Neurons AI Pvt Ltd · All rights reserved</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/posts" element={<PostsPage />} />
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:id" element={<BlogDetailPage />} />
        <Route path="/*" element={<NewsHome />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
